import React, { useState } from "react";
import { Box, Stack, TextField, MenuItem, CircularProgress } from "@mui/material";
import Modal, { ModalHead, ModalBody, ModalFoot } from "./Modal";
import BtnPrimary from "./PrimaryButton";
import BtnSecondary from "./SecondaryButton";
import ErrorMsg from "./ErrorMsg";
import InlineMessage from "./InlineMessage";
import { tokens } from "../theme";

const REASONS = ["Resigned", "Contract ending", "Retirement", "Mutual separation", "Terminated", "Other"];

const iso = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");
const longDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";

/**
 * Putting somebody on notice.
 *
 * It used to be a button that fired immediately: one click, status changed,
 * nothing recorded and no end date. Notice is a period with an end — that is
 * what the word means — and an employee sitting on notice with no last working
 * day is one payroll cannot settle and no relieving letter can describe.
 *
 * So the dialog asks for the three things an exit actually needs: why, when it
 * takes effect, and when they finish. The last day is computed from the
 * employee's notice period and shown, not hidden — somebody negotiating an
 * earlier release needs to see what the default was before they change it.
 *
 * It also says what will happen, because the consequences are not obvious from
 * a button: a checklist gets assigned, and payroll will prorate to that date.
 */
const StartNoticeDialog = ({ employee, open, onClose, onConfirm, onRequest }) => {
  const noticeDays = Number(employee?.noticePeriod) || 0;
  // A resignation already on file. Accepting it is a different act from
  // recording one — the date is already proposed and the question is only
  // whether to agree it.
  const pending = employee?.exitRequest || null;

  const defaultLastDay = () => {
    const from = new Date();
    if (!noticeDays) return "";
    from.setDate(from.getDate() + noticeDays);
    return iso(from);
  };

  const [reason, setReason] = useState(pending?.reason || "Resigned");
  const [effectiveDate, setEffectiveDate] = useState(iso(pending?.requestedAt || new Date()));
  const [lastDay, setLastDay] = useState(
    pending?.proposedLastWorkingDay ? iso(pending.proposedLastWorkingDay) : defaultLastDay()
  );
  const [note, setNote] = useState(pending?.note || "");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  // Changing when notice starts moves the end with it, unless somebody has
  // already set the end themselves — an agreed last day is not a derived one.
  const [lastDayTouched, setLastDayTouched] = useState(false);
  const changeEffective = (value) => {
    setEffectiveDate(value);
    setErrors((p) => ({ ...p, effectiveDate: "" }));
    if (!lastDayTouched && noticeDays && value) {
      const d = new Date(value);
      d.setDate(d.getDate() + noticeDays);
      setLastDay(iso(d));
    }
  };

  // Record without deciding. Only the reason and the proposed date matter —
  // when notice starts is the approver's to set, later.
  const record = async () => {
    const e = {};
    if (!lastDay) e.lastDay = "When do they propose to finish?";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await onRequest({ reason, proposedLastWorkingDay: lastDay, note: note.trim() || undefined });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const e = {};
    if (!effectiveDate) e.effectiveDate = "When does notice start?";
    if (!lastDay) e.lastDay = "When is their last working day?";
    if (effectiveDate && lastDay && new Date(lastDay) < new Date(effectiveDate))
      e.lastDay = "The last working day cannot be before notice starts";
    setErrors(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      await onConfirm({ reason, effectiveDate, lastWorkingDay: lastDay, note: note.trim() || undefined });
    } finally {
      setBusy(false);
    }
  };

  const name = employee?.fname || "this employee";

  return (
    <Modal open={open} onClose={onClose} width="32rem">
      <ModalHead
        title={pending ? "Accept this resignation?" : "Start notice period"}
        subtitle={`${employee?.fname || ""} ${employee?.lname || ""}`.trim()}
        onClose={onClose}
      />
      <ModalBody>
        {pending && (
          <Box sx={{ mb: "1rem" }}>
            <InlineMessage tone="info">
              A resignation was recorded on{" "}
              {longDate(pending.requestedAt)}. Accepting it starts notice; the dates
              below are what was proposed and can be changed.
            </InlineMessage>
          </Box>
        )}

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>Reason *</Box>
        <TextField select fullWidth size="small" value={reason}
          onChange={(e) => setReason(e.target.value)} sx={{ mb: "1rem" }}>
          {REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: errors.lastDay ? 0 : "1rem" }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
              Notice starts *
            </Box>
            <TextField fullWidth size="small" type="date" value={effectiveDate}
              onChange={(e) => changeEffective(e.target.value)} error={Boolean(errors.effectiveDate)} />
            <ErrorMsg>{errors.effectiveDate}</ErrorMsg>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
              Last working day *
            </Box>
            <TextField fullWidth size="small" type="date" value={lastDay}
              onChange={(e) => { setLastDay(e.target.value); setLastDayTouched(true); setErrors((p) => ({ ...p, lastDay: "" })); }}
              error={Boolean(errors.lastDay)} />
            <ErrorMsg>{errors.lastDay}</ErrorMsg>
          </Box>
        </Stack>

        <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mb: "1rem" }}>
          {noticeDays
            ? `${name}'s notice period is ${noticeDays} days, so the last day is set to ${longDate(lastDay)}. Change it if a different date was agreed.`
            : `No notice period is recorded for ${name}, so the last working day has to be entered. You can set one on their profile for next time.`}
        </Box>

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>Note (optional)</Box>
        <TextField fullWidth size="small" multiline minRows={2} value={note}
          placeholder="Anything worth recording alongside the reason"
          onChange={(e) => setNote(e.target.value)} sx={{ mb: "1.25rem" }} />

        {/* What happens next, said rather than discovered. */}
        <InlineMessage tone="info">
          <Box component="ul" sx={{ m: 0, pl: "1.1rem" }}>
            <li>Their exit checklist is assigned, with dates counted back from the last working day.</li>
            <li>Payroll will prorate their final pay to {longDate(lastDay)}.</li>
            <li>They keep their access until the last day — this does not deactivate the account.</li>
          </Box>
        </InlineMessage>
      </ModalBody>
      <ModalFoot
        note={
          !pending && onRequest
            ? "“Record only” keeps the reason, last day and note, and leaves their status alone until somebody accepts it."
            : "Recorded on their career timeline, with who did it and when."
        }
      >
        <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
        {/* Recording a resignation and accepting it are separate acts: somebody
            resigns, the company decides, and in between they are working
            normally and it may still not happen. Offered as two buttons rather
            than one, unless a request is already on file — then the decision
            is the only thing left to make. */}
        {!pending && onRequest && (
          <BtnSecondary onClick={() => record()} disabled={busy} sx={{ borderRadius: "8px" }}>
            Record only
          </BtnSecondary>
        )}
        <BtnPrimary onClick={submit} disabled={busy} sx={{ borderRadius: "8px" }}>
          {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : pending ? "Accept and start notice" : "Start notice"}
        </BtnPrimary>
      </ModalFoot>
    </Modal>
  );
};

export default StartNoticeDialog;
